import styled, { css, keyframes } from 'styled-components';
import { rgba } from 'polished';
import { SETUP_EXIT_MS } from './SetupStatus.constants';

/* ── Layout ─────────────────────────────────────────────────────── */

export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.surface};
  overflow: hidden;
`;

export const MessagesWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  min-height: 0;
`;

export const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  /* Prevent iOS Safari from bouncing the outer page when scroll reaches the
     top or bottom of the conversation — keep overscroll contained here. */
  overscroll-behavior: contain;
`;

export const MessagesContent = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.level6} ${({ theme }) => theme.spacing.level5};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.level6};

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.level4} ${({ theme }) => theme.spacing.level3};
    gap: ${({ theme }) => theme.spacing.level5};
  }
`;

/* ── Infinite scroll trigger ────────────────────────────────────── */

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const EmptyStateSlot = styled.div`
  padding-top: ${({ theme }) => theme.spacing.level6};
`;

export const LoadMoreTrigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.level3} 0;
  min-height: 1px;
`;

export const LoadMoreSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${({ theme }) => theme.colors.outlineVariant};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

/* ── Message row ────────────────────────────────────────────────── */

export const MessageWrapper = styled.div<{ $role: 'user' | 'assistant' | 'system' }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.level3};
  width: 100%;
  justify-content: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
`;

export const MessageColumn = styled.div<{ $role: 'user' | 'assistant' | 'system' }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  gap: ${({ theme }) => theme.spacing.level1};
  min-width: 0;
  max-width: 80%;
  ${({ $role }) => $role === 'assistant' ? 'width: 80%;' : ''}

  @media (max-width: 768px) {
    max-width: 90%;
    ${({ $role }) => $role === 'assistant' ? 'width: 90%;' : ''}
  }
`;

/* ── Avatars / badges ───────────────────────────────────────────── */

export const AIBadge = styled.div`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.level1};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: ${({ theme }) => (theme.isLightTheme ? 'brightness(0)' : 'none')};
  }
`;

export const UserBadge = styled.div`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.surfaceContainerHigh};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin-top: ${({ theme }) => theme.spacing.level1};

  svg {
    width: 18px;
    height: 18px;
    fill: ${({ theme }) => theme.colors.onSurfaceVariant};
  }
`;

/* ── Bubbles & content ──────────────────────────────────────────── */

const markdownListStyles = css`
  ol, ul {
    margin: ${({ theme }) => theme.spacing.level2} 0;
    padding-left: ${({ theme }) => theme.spacing.level5};
  }

  li {
    margin: 0;
    padding: 0;
  }

  li + li {
    margin-top: ${({ theme }) => theme.spacing.level1};
  }

  li > ol, li > ul {
    margin: ${({ theme }) => theme.spacing.level1} 0;
  }
`;

export const MessageBubble = styled.div<{ $role: 'user' | 'assistant' }>`
  padding: ${({ theme }) => theme.spacing.level4} ${({ theme }) => theme.spacing.level5};
  border-radius: 1rem calc(1rem - 4px) 1rem 1rem;
  background-color: ${({ theme }) => theme.colors.secondaryContainer};
  color: ${({ theme }) => theme.colors.onSecondaryContainer};
  max-width: 100%;
  line-height: 1.5;
  ${({ theme }) => theme.font.bodyMedium};

  p {
    margin: 0;
  }

  /* If there are multiple paragraphs, small gap */
  p + p {
    margin-top: ${({ theme }) => theme.spacing.level2};
  }

  ${markdownListStyles}

  /* Links: use the surface's own text colour + underline so they're readable on both
     light (lavender) and dark (navy) variants of the bubble background.
     The browser default blue is nearly invisible on the dark-mode bubble. */
  a {
    color: ${({ theme }) => theme.colors.onSecondaryContainer};
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      opacity: 0.75;
    }
  }
`;

const tightMarkdownStyles = css`
  ${({ theme }) => theme.font.bodyMedium};
  color: ${({ theme }) => theme.colors.onSurface};
  line-height: 1.5;

  /* Paragraphs: no gap to first, minimal between */
  p {
    margin: 0;
  }

  p + p {
    margin-top: ${({ theme }) => theme.spacing.level3};
  }

  /* Headings: compact */
  h1, h2, h3, h4, h5, h6 {
    margin: ${({ theme }) => theme.spacing.level4} 0 ${({ theme }) => theme.spacing.level2} 0;
    line-height: 1.35;
    font-weight: 600;
  }

  h1:first-child, h2:first-child, h3:first-child,
  h4:first-child, h5:first-child, h6:first-child {
    margin-top: 0;
  }

  ${markdownListStyles}

  /* Code */
  code {
    background: ${({ theme }) => theme.colors.surfaceContainerHighest};
    padding: 1px ${({ theme }) => theme.spacing.level2};
    border-radius: ${({ theme }) => theme.borderRadius.extrasmall};
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    font-size: 0.88em;
  }

  pre {
    background: ${({ theme }) => theme.colors.surfaceContainerHighest};
    padding: ${({ theme }) => theme.spacing.level3};
    border-radius: ${({ theme }) => theme.borderRadius.small};
    overflow-x: auto;
    margin: ${({ theme }) => theme.spacing.level3} 0;

    code {
      background: transparent;
      padding: 0;
    }
  }

  /* Blockquote */
  blockquote {
    border-left: 3px solid ${({ theme }) => theme.colors.primary};
    padding-left: ${({ theme }) => theme.spacing.level3};
    margin: ${({ theme }) => theme.spacing.level3} 0;
    opacity: 0.9;
  }

  /* Links */
  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }

  /* Horizontal rules */
  hr {
    border: none;
    border-top: 1px solid ${({ theme }) => theme.colors.outlineVariant};
    margin: ${({ theme }) => theme.spacing.level3} 0;
  }

  /* Strong / emphasis */
  strong {
    font-weight: 600;
  }

  /* Tables */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: ${({ theme }) => theme.spacing.level3} 0;
    font-size: 0.9em;
    overflow-x: auto;
    display: block;
  }

  thead {
    background: ${({ theme }) => theme.colors.surfaceContainerHighest};
  }

  th, td {
    padding: ${({ theme }) => theme.spacing.level2} ${({ theme }) => theme.spacing.level3};
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.outlineVariant};
    vertical-align: top;
    line-height: 1.4;
  }

  th {
    font-weight: 600;
    white-space: nowrap;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.colors.surfaceContainerHigh};
  }
`;

export const MessageContent = styled.div`
  max-width: 100%;
  width: 100%;
  ${tightMarkdownStyles}
`;

/* ── Timestamp ──────────────────────────────────────────────────── */

export const MessageTime = styled.span`
  ${({ theme }) => theme.font.labelSmall};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  opacity: 0.6;
  margin-top: ${({ theme }) => theme.spacing.level3};
`;

/* ── Thinking indicator ─────────────────────────────────────────── */

const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-6px); }
`;

export const ThinkingBubble = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.level3};
  padding: ${({ theme }) => theme.spacing.level4} ${({ theme }) => theme.spacing.level5};
  border-radius: 20px 20px 20px 4px;
  background-color: ${({ theme }) => theme.colors.surfaceContainerHigh};
`;

export const ThinkingDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const ThinkingDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  animation: ${bounce} 1.4s ease-in-out infinite;

  &:nth-child(1) { animation-delay: 0s; }
  &:nth-child(2) { animation-delay: 0.16s; }
  &:nth-child(3) { animation-delay: 0.32s; }
`;

export const ThinkingLabel = styled.span`
  ${({ theme }) => theme.font.bodyMedium};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
`;

export const InlineThinking = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${({ theme }) => theme.spacing.level3} 0;
`;

export const InlineThinkingDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  animation: ${bounce} 1.4s ease-in-out infinite;

  &:nth-child(1) { animation-delay: 0s; }
  &:nth-child(2) { animation-delay: 0.16s; }
  &:nth-child(3) { animation-delay: 0.32s; }
`;

export const InlineThinkingLabel = styled.span`
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  opacity: 0.7;
`;

/* ── Tool chip ─────────────────────────────────────────────────── */

export const ToolChipWrapper = styled.div`
  margin: ${({ theme }) => theme.spacing.level4} 0;
  display: block;
  pointer-events: none;
`;

type ToolChipColors = { success: string; error: string; onSurfaceVariant: string; onSuccessContainer?: string; onErrorContainer: string };

const toolChipBgColor = (status: string, success: boolean | undefined, colors: ToolChipColors): string => {
  if (status === 'complete' && success) return `background-color: ${colors.success};`;
  if (status === 'complete') return `background-color: ${colors.error};`;
  return `background-color: ${colors.onSurfaceVariant};`;
};

const toolChipFgColor = (status: string, success: boolean | undefined, colors: ToolChipColors): string => {
  if (status === 'complete' && success) return colors.onSuccessContainer || colors.success;
  if (status === 'complete') return colors.onErrorContainer;
  return colors.onSurfaceVariant;
};

// Needed for ToolChipInner $status prop compatibility (pending_approval treated as executing visually)
export type ToolChipStatus = 'executing' | 'complete' | 'pending_approval';

export const ToolChipInner = styled.div<{ $status: 'executing' | 'complete' | 'pending_approval'; $success?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.level3};
  padding: ${({ theme }) => theme.spacing.level4} ${({ theme }) => theme.spacing.level5};
  border-radius: ${({ theme }) => theme.borderRadius?.small || '0.5rem'};
  ${({ theme }) => theme.font.bodyMedium};
  white-space: nowrap;
  position: relative;
  overflow: hidden;

  /* Subtle tinted background matching MessageBox StateLayer (16% opacity) */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    opacity: 0.16;
    ${({ $status, $success, theme }) => toolChipBgColor($status, $success, theme.colors as unknown as ToolChipColors)}
  }

  color: ${({ $status, $success, theme }) => toolChipFgColor($status, $success, theme.colors as unknown as ToolChipColors)};
`;

const toolChipSpin = keyframes`
  to { transform: rotate(360deg); }
`;

export const ToolChipSpinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid ${({ theme }) => theme.colors.onSurfaceVariant}30;
  border-top-color: ${({ theme }) => theme.colors.onSurfaceVariant};
  border-radius: 50%;
  animation: ${toolChipSpin} 0.7s linear infinite;
  flex-shrink: 0;
`;

export const ToolChipIcon = styled.span<{ $success?: boolean }>`
  display: flex;
  align-items: center;
  font-size: 18px;
  flex-shrink: 0;
  color: ${({ $success, theme }) =>
    $success ? theme.colors.success : theme.colors.error};
`;

/* ── Tool image preview (expandable screenshot) ───────────────── */

export const ToolImageWrapper = styled.div`
  margin: ${({ theme }) => theme.spacing.level4} 0;
  display: block;
  pointer-events: auto;
`;

export const ToolImageHeader = styled.button<{ $status: 'executing' | 'complete' | 'pending_approval'; $success?: boolean }>`
  display: inline-flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.level3};
  padding: ${({ theme }) => theme.spacing.level4} ${({ theme }) => theme.spacing.level5};
  border-radius: ${({ theme }) => theme.borderRadius?.small || '0.5rem'};
  ${({ theme }) => theme.font.bodyMedium};
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  border: none;
  background: ${({ theme }) => theme.colors.surfaceContainerHigh};
  color: ${({ theme }) => theme.colors.onSurface};

  > svg {
    margin-top: 0.13rem;
  }
`;

export const ToolHeaderStatic = styled(ToolImageHeader).attrs({ as: 'div' as const })`
  cursor: default;
`;

export const ToolChipSummary = styled.span`
  display: block;
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
`;

export const ToolImageChevron = styled.span<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  margin-left: auto;
  font-size: 16px;
  transition: transform 0.2s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
`;

export const ToolImagePreviewContainer = styled.div<{ $expanded: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? '2000px' : '0')};
  overflow: hidden;
  overflow-y: ${({ $expanded }) => ($expanded ? 'auto' : 'hidden')};
  transition: max-height 0.4s ease;
  padding: 0 ${({ theme }) => theme.spacing.level6};

  > * {
    margin: ${({ theme }) => theme.spacing.level6} 0;
  }
`;

export const CodeChipWrapper = styled.div`
  margin: ${({ theme }) => theme.spacing.level4} 0;
  border-radius: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.surfaceContainerHigh};
  overflow: hidden;

  > ${ToolImageHeader} {
    display: flex;
    width: 100%;
    border-radius: 0;
  }
`;

export const CodePreviewContainer = styled.div<{ $expanded: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? '400px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

export const ToolImageThumbnail = styled.img`
  display: block;
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius?.small || '0.5rem'};
  cursor: zoom-in;
  object-fit: contain;
`;

export const Model3DPreviewCanvas = styled.div`
  display: block;
  width: 100%;
  height: 400px;
  border-radius: ${({ theme }) => theme.borderRadius?.small || '0.5rem'};
  cursor: zoom-in;
  overflow: hidden;
`;

/* ── Artifact card ─────────────────────────────────────────────── */

export const ArtifactChip = styled.div`
  margin: ${({ theme }) => theme.spacing.level4} 0;
  border-radius: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.surfaceContainerHigh};
  background: ${({ theme }) => theme.colors.surface};
  width: 100%;
  overflow: hidden;
`;

export const ArtifactCardContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.level4};
  padding: ${({ theme }) => theme.spacing.level6};
`;

export const ArtifactCardIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius?.small || '0.5rem'};
  background: ${({ theme }) => theme.colors.primaryContainer};
  color: ${({ theme }) => theme.colors.onPrimaryContainer};
  font-size: 20px;
  flex-shrink: 0;
`;

export const ArtifactCardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ArtifactFileName = styled.div`
  ${({ theme }) => theme.font.bodyMedium};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.onSurface};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ArtifactDescription = styled.div`
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  margin-top: 3px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const ArtifactMeta = styled.div`
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  opacity: 0.7;
  margin-top: 2px;
`;

export const ArtifactLocalPath = styled.div`
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  opacity: 0.6;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  direction: rtl;
  text-align: left;
`;

export const ArtifactDownloadButton = styled.a<{ $downloading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  text-decoration: none;
  flex-shrink: 0;
  transition: opacity 0.15s;
  font-size: 18px;
  border: none;
  cursor: ${({ $downloading }) => ($downloading ? 'wait' : 'pointer')};

  &:hover {
    opacity: 0.85;
  }
`;

/* ── Error block ────────────────────────────────────────────────── */

export const ErrorWrapper = styled.div`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.level4};
`;

export const ErrorSupportText = styled.span`
  display: block;
  margin-top: ${({ theme }) => theme.spacing.level2};
  font-size: 13px;
  opacity: 0.85;
`;

/* ── Artifact inline image preview ─────────────────────────────── */

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
`;

const skeletonBase = css`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.surfaceContainerHigh} 25%,
    ${({ theme }) => theme.colors.surfaceContainer}     50%,
    ${({ theme }) => theme.colors.surfaceContainerHigh} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite linear;
`;

export const ArtifactImageWrapper = styled.div`
  position: relative;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colors.outlineVariant};
`;

export const ArtifactImageSkeleton = styled.div`
  ${skeletonBase}
  width: 100%;
  /* Reserve approximate height of a screenshot image before it loads */
  aspect-ratio: 16 / 9;
`;

export const ArtifactImagePreview = styled.img<{ $loaded: boolean }>`
  display: ${({ $loaded }) => ($loaded ? 'block' : 'none')};
  width: 100%;
  cursor: pointer;
`;

export const ModelViewerSkeleton = styled.div`
  ${skeletonBase}
  width: 100%;
  height: 100%;
`;

export const ArtifactCollapseButton = styled.button<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: transparent;
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s, transform 0.2s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});

  &:hover {
    color: ${({ theme }) => theme.colors.onSurface};
  }
`;

export const ArtifactPreviewCollapse = styled.div<{ $expanded: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? '2000px' : '0')};
  overflow: hidden;
  overflow-y: ${({ $expanded }) => ($expanded ? 'auto' : 'hidden')};
  transition: max-height 0.4s ease;
`;

/* ── 3D model viewer ────────────────────────────────────────────── */

export const ModelPreviewContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border-top: 1px solid ${({ theme }) => theme.colors.outlineVariant};

  canvas {
    display: block;
    width: 100% !important;
    height: 100% !important;
  }
`;

export const ModelPreviewExpandButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.45);
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s ease, background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
  }

  ${ModelPreviewContainer}:hover & {
    opacity: 1;
  }
`;

export const PdfPreviewContainer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-top: 1px solid ${({ theme }) => theme.colors.outlineVariant};
`;

export const LoadModelButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.level2};
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.level3};
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level4};
  border-radius: ${({ theme }) => theme.borderRadius?.small || '0.5rem'};
  border: 1px dashed ${({ theme }) => theme.colors.outlineVariant};
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  ${({ theme }) => theme.font.bodySmall};
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryContainer};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const ModelPreviewFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.level2};
  width: 100%;
  height: 100%;
  padding: ${({ theme }) => theme.spacing.level3};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  ${({ theme }) => theme.font.bodySmall};
  text-align: center;
`;

export const ModelPreviewRetryButton = styled.button`
  padding: ${({ theme }) => theme.spacing.level2} ${({ theme }) => theme.spacing.level4};
  border-radius: ${({ theme }) => theme.borderRadius?.small || '0.5rem'};
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  ${({ theme }) => theme.font.bodySmall};
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryContainer};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

/* ── Markdown figure/figcaption ───────────────────────────────── */

export const MarkdownFigure = styled.figure`
  margin: ${({ theme }) => theme.spacing.level4} 0;

  &:first-child {
    margin-top: 0;
  }
`;

export const MarkdownFigcaption = styled.figcaption`
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  margin-top: ${({ theme }) => theme.spacing.level1};
  text-align: center;
`;

export const MarkdownImg = styled.img`
  max-width: 100%;
`;

/* ── Attachment preview ─────────────────────────────────────────── */

export const AttachmentPreviewArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.level2};
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level3} 0;
  margin-bottom: ${({ theme }) => theme.spacing.level4};
`;

/* ── Message attachment display (shown above the bubble) ────────── */

export const MessageAttachments = styled.div<{ $role: 'user' | 'assistant' }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.level2};
  margin-bottom: ${({ theme }) => theme.spacing.level2};
  justify-content: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
`;

export const LegacyStepRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.level3};
  padding: ${({ theme }) => theme.spacing.level3} 0;

  /* Divider only BETWEEN steps, not above the first one */
  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.outlineVariant}30;
  }
`;

export const LegacyStepNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  ${({ theme }) => theme.font.labelSmall};
  font-size: 11px;
  flex-shrink: 0;
  margin-top: 2px;
`;

export const LegacyStepContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const LegacyStepAgent = styled.span`
  ${({ theme }) => theme.font.labelSmall};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
`;

export const LegacyStepTask = styled.span`
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme }) => theme.colors.onSurface};
  display: block;
  margin-top: 2px;
`;

/* ── Scope confirmation card ───────────────────────────────────── */

export const ScopeCardContainer = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  overflow: hidden;
  margin-top: ${({ theme }) => theme.spacing.level5};
`;

export const ScopeCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.level3};
  padding: ${({ theme }) => theme.spacing.level4} ${({ theme }) => theme.spacing.level5};
`;

export const ScopeCardIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 16px;
  flex-shrink: 0;

  .material-symbols-outlined {
    font-size: inherit;
  }
`;

export const ScopeCardIconSymbol = styled.span`
  font-size: 16px;
`;

export const ScopeCardTitle = styled.span`
  ${({ theme }) => theme.font.titleSmall};
  color: ${({ theme }) => theme.colors.onSurface};
  flex: 1;
`;

export const ScopeCardActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.level3};
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level5} ${({ theme }) => theme.spacing.level4};
  border-top: 1px solid ${({ theme }) => theme.colors.outlineVariant}30;
`;

export const ScopeProceedButton = styled.button`
  ${({ theme }) => theme.font.labelMedium};
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level5};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ScopeRejectButton = styled.button`
  ${({ theme }) => theme.font.labelMedium};
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level5};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  background: transparent;
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceContainerHighest};
  }
`;

/* ── Setup status ──────────────────────────────────────────────── */

const setupStepIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const SetupStatusWrapper = styled.div<{ $exiting: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.level2};
  margin: ${({ theme }) => theme.spacing.level4} 0;
  overflow: hidden;
  transition: opacity ${SETUP_EXIT_MS}ms ease, max-height ${SETUP_EXIT_MS}ms ease,
    margin ${SETUP_EXIT_MS}ms ease;
  max-height: 200px;

  ${({ $exiting }) => $exiting && css`
    opacity: 0;
    max-height: 0;
    margin: 0;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const SetupStepRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.level2};
  animation: ${setupStepIn} 180ms ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SetupStepPill = styled.div<{ $complete: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.level3};
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level5};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  ${({ theme }) => theme.font.bodyMedium};
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  color: ${({ $complete, theme }) =>
    $complete ? theme.colors.primary : theme.colors.onSurfaceVariant};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    opacity: 0.12;
    background-color: ${({ $complete, theme }) =>
      $complete ? theme.colors.primary : theme.colors.onSurfaceVariant};
  }
`;

export const SetupStepHint = styled.span`
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  opacity: 0.7;
`;

export const SkillLoadChipWrapper = styled.div`
  display: flex;
  margin: ${({ theme }) => theme.spacing.level4} 0;
`;

/* ── Compaction banner ─────────────────────────────────────────── */

export const CompactingBanner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.level3};
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level5};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  ${({ theme }) => theme.font.bodyMedium};
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.onSurfaceVariant};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    opacity: 0.12;
    background-color: ${({ theme }) => theme.colors.onSurfaceVariant};
  }
`;


export const StepExecutionChip = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  overflow: hidden;
  margin: ${({ theme }) => theme.spacing.level4} 0;
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level5};
`;

/* ── Input section ──────────────────────────────────────────────── */

export const InputSection = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.surface};
  flex-shrink: 0;
  padding: ${({ theme }) => theme.spacing.level4} 0 ${({ theme }) => theme.spacing.level3};
`;

export const InputSectionContent = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.level5};

  @media (max-width: 768px) {
    padding: 0 ${({ theme }) => theme.spacing.level3};
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const InputWrapper = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing.level4} ${({ theme }) => theme.spacing.level5} ${({ theme }) => theme.spacing.level5};
`;

export const InputContainer = styled.div`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.surfaceContainerHigh};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing.level2} ${({ theme }) => theme.spacing.level3};
  gap: ${({ theme }) => theme.spacing.level2};
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant}30;
  transition: border-color 0.2s ease;
  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary}50;
  }
`;

export const Input = styled.textarea<{ $heightPx?: number; $scrollable?: boolean }>`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.colors.onSurface};
  ${({ theme }) => theme.font.bodyMedium};
  padding: ${({ theme }) => theme.spacing.level2} ${({ theme }) => theme.spacing.level3};
  resize: none;
  overflow-y: ${({ $scrollable }) => ($scrollable ? 'auto' : 'hidden')};
  min-height: 24px;
  max-height: 200px;
  height: ${({ $heightPx }) => ($heightPx == null ? 'auto' : `${$heightPx}px`)};
  font-family: inherit;

  &::placeholder {
    color: ${({ theme }) => theme.colors.onSurfaceVariant};
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const RunnerPickerRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level4} 0;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.level2};

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.level2} ${({ theme }) => theme.spacing.level3} 0;
  }
`;

export const RunnerPickerContainer = styled.div`
  position: relative;
  display: inline-flex;
`;

export const RunnerPickerTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.level2};
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  background: transparent;
  border: none;
  padding: ${({ theme }) => theme.spacing.level1} ${({ theme }) => theme.spacing.level2};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  cursor: pointer;
  transition: background-color 0.15s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceContainerHigh};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg:first-child {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  svg:last-child {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }
`;

export const RunnerPickerMenu = styled.ul<{ $menuDirection?: 'up' | 'down' }>`
  position: absolute;
  ${({ $menuDirection, theme }) =>
    $menuDirection === 'down'
      ? `top: calc(100% + ${theme.spacing.level2}); bottom: auto;`
      : `bottom: calc(100% + ${theme.spacing.level2}); top: auto;`}
  left: 0;
  z-index: 10;
  min-width: 200px;
  max-height: 240px;
  overflow-y: auto;
  margin: 0;
  padding: ${({ theme }) => theme.spacing.level3} 0;
  list-style: none;
  background: ${({ theme }) => theme.colors.surfaceContainerHigh};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.3), 0px 2px 6px 2px rgba(0, 0, 0, 0.15);
`;

export const RunnerPickerItem = styled.li<{ $selected?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.level4};
  padding: ${({ theme }) => `${theme.spacing.level3} ${theme.spacing.level4}`};
  min-height: 56px;
  ${({ theme }) => theme.font.bodyLarge};
  color: ${({ $selected, theme }) =>
    $selected ? theme.colors.onSecondaryContainer : theme.colors.onSurface};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.38 : 1)};
  transition: background-color 0.1s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.secondaryContainer : 'transparent'};

  &:hover {
    background: ${({ $disabled, $selected, theme }) => {
      if ($disabled) return 'transparent';
      if ($selected) return theme.colors.secondaryContainer;
      return rgba(theme.colors.onSurface, 0.08);
    }};
  }

  svg {
    width: ${({ theme }) => theme.spacing.level6};
    height: ${({ theme }) => theme.spacing.level6};
    flex-shrink: 0;
    color: ${({ $selected, theme }) =>
      $selected ? theme.colors.onSecondaryContainer : theme.colors.onSurface};
  }
`;

export const DisclaimerText = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.level3};
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  opacity: 0.5;
`;

export const BetaBanner = styled.div`
  text-align: center;
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
  opacity: 0.5;
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level5};
`;

export const JumpToBottomButton = styled.button`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing.level4};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: ${({ theme }) => theme.colors.onSurface};
  background: ${({ theme }) => theme.colors.surfaceContainerHigh};
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  z-index: 10;
  transition: background-color 0.15s ease;
  padding: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceContainer};
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

export const FileErrorText = styled.div`
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme }) => theme.colors.error};
  margin-top: ${({ theme }) => theme.spacing.level2};
  padding: 0 ${({ theme }) => theme.spacing.level2};
`;

export const CharacterCount = styled.div<{ $over: boolean }>`
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ theme, $over }) => ($over ? theme.colors.error : theme.colors.onSurfaceVariant)};
  align-self: flex-end;
  padding: 0 ${({ theme }) => theme.spacing.level2};
`;

// ── Approval Mode Picker (right-aligned in RunnerPickerRow) ───────────────────

export const ApprovalPickerContainer = styled.div<{ $field?: boolean }>`
  position: relative;
  display: inline-flex;
  margin-left: auto;

  /* The menu anchors to the picker row instead, which is the only box wide enough on a phone. */
  @media (max-width: 768px) {
    position: static;
    margin-left: 0;
  }

  ${({ $field }) =>
    $field &&
    css`
      display: flex;
      margin-left: 0;

      @media (max-width: 768px) {
        position: relative;
      }
    `}
`;

const fieldTrigger = css`
  width: 100%;
  height: 32px;
  padding: 0 ${({ theme }) => theme.spacing.level3};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.onSurface};
  background: ${({ theme }) => theme.colors.surfaceContainerLow};
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.outlineVariant};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceContainerLow};
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.outline};
  }

  svg:last-child {
    margin-left: auto;
  }
`;

export const ApprovalPickerTrigger = styled.button<{ $warn?: boolean; $field?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.level2};
  ${({ theme }) => theme.font.bodySmall};
  color: ${({ $warn, theme }) => ($warn ? theme.colors.error : theme.colors.onSurfaceVariant)};
  background: transparent;
  border: none;
  padding: ${({ theme }) => theme.spacing.level1} ${({ theme }) => theme.spacing.level2};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  cursor: pointer;
  transition: background-color 0.15s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceContainerHigh};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg:first-child {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  svg:last-child {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  ${({ $field }) => $field && fieldTrigger}
`;

export const ApprovalPickerMenu = styled.ul<{ $menuDirection?: 'up' | 'down'; $field?: boolean }>`
  position: absolute;
  ${({ $menuDirection, theme }) =>
    $menuDirection === 'down'
      ? `top: calc(100% + ${theme.spacing.level2}); bottom: auto;`
      : `bottom: calc(100% + ${theme.spacing.level2}); top: auto;`}
  right: 0;
  z-index: 10;

  @media (max-width: 768px) {
    left: 0;
    right: 0;
    min-width: 0;
    max-height: min(60vh, 420px);
    overflow-y: auto;
  }
  min-width: 280px;
  margin: 0;
  padding: ${({ theme }) => theme.spacing.level2} 0;
  list-style: none;
  background: ${({ theme }) => theme.colors.surfaceContainerHigh};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.3), 0px 2px 6px 2px rgba(0, 0, 0, 0.15);

  ${({ $field }) =>
    $field &&
    css`
      left: 0;
      min-width: 0;
      max-height: min(50vh, 420px);
      overflow-y: auto;
    `}
`;

export const ApprovalPickerItem = styled.li<{ $selected?: boolean; $warn?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.level3};
  padding: ${({ theme }) => `${theme.spacing.level2} ${theme.spacing.level4}`};
  min-height: 36px;
  ${({ theme }) => theme.font.bodyMedium};
  color: ${({ $warn, $selected, theme }) => {
    if ($warn) return theme.colors.error;
    if ($selected) return theme.colors.onSecondaryContainer;
    return theme.colors.onSurface;
  }};
  cursor: pointer;
  transition: background-color 0.1s ease;
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.secondaryContainer : 'transparent'};

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? theme.colors.secondaryContainer : rgba(theme.colors.onSurface, 0.08)};
  }

  svg {
    width: ${({ theme }) => theme.spacing.level5};
    height: ${({ theme }) => theme.spacing.level5};
    flex-shrink: 0;
    margin-top: 2px;
    color: ${({ $warn, $selected, theme }) => {
      if ($warn) return theme.colors.error;
      if ($selected) return theme.colors.onSecondaryContainer;
      return theme.colors.onSurfaceVariant;
    }};
  }
`;

export const ApprovalPickerItemContent = styled.span`
  display: flex;
  flex-direction: column;
`;

export const ApprovalPickerItemLabel = styled.span`
  ${({ theme }) => theme.font.bodyMedium};
`;

export const ApprovalPickerItemDesc = styled.span`
  ${({ theme }) => theme.font.bodySmall};
  opacity: 0.76;
  white-space: normal;
`;

// ── Pending Approval Tool Chip ────────────────────────────────────────────────

export const ApprovalChipWrapper = styled.div`
  margin: ${({ theme }) => theme.spacing.level4} 0;
  pointer-events: all;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  overflow: hidden;
  max-width: 560px;
`;

export const ApprovalChipHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.level3};
  padding: ${({ theme }) => theme.spacing.level4} ${({ theme }) => theme.spacing.level5};
  ${({ theme }) => theme.font.titleSmall};
  color: ${({ theme }) => theme.colors.onSurface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.outlineVariant}30;

  svg {
    color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
  }
`;

export const ApprovalCodeBlockWrapper = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.level5} ${({ theme }) => theme.spacing.level4};
`;

export const ApprovalCodeBlock = styled.pre`
  && {
    margin: ${({ theme }) => theme.spacing.level4};
    padding: ${({ theme }) => theme.spacing.level5};
    border-radius: ${({ theme }) => theme.borderRadius.small};
    ${({ theme }) => theme.font.bodySmall};
    font-family: 'Fira Code', 'Consolas', 'Monaco', monospace;
    background: transparent;
    border: none;
    color: ${({ theme }) => theme.colors.onSurface};
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 280px;
    overflow-y: auto;
  }
`;

export const ApprovalActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.level3};
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level5} ${({ theme }) => theme.spacing.level4};
  border-top: 1px solid ${({ theme }) => theme.colors.outlineVariant}30;
`;

export const RejectionCommentArea = styled.div`
  padding: ${({ theme }) => theme.spacing.level3} ${({ theme }) => theme.spacing.level5};
  border-top: 1px solid ${({ theme }) => theme.colors.outlineVariant}30;
`;

export const RejectionCommentInput = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing.level2} ${({ theme }) => theme.spacing.level3};
  border: 1px solid ${({ theme }) => theme.colors.outlineVariant};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme }) => theme.colors.surfaceContainerLow};
  color: ${({ theme }) => theme.colors.onSurface};
  ${({ theme }) => theme.font.bodySmall};
  resize: vertical;
  min-height: 60px;
  max-height: 120px;
  outline: none;
  font-family: inherit;

  &::placeholder {
    color: ${({ theme }) => theme.colors.onSurfaceVariant};
    opacity: 0.7;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;
